import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import IconMC from "../../components/IconMC";
import ButtonComponent from "../../components/ButtonMC";
import CallCustomerData from './CallCustomerData';
import { useButtonsDisabled } from "../../hooks/shared/ui/useButtonsDisabled";
import useFonemaCallDuration from "../../hooks/fonema/ui/useFonemaCallDuration";
import {
  useWhatsappCallsRuntime,
  useCallUiEvents
} from "../../hooks/calls/WhatsappCallsRuntimeContext";
import useWhatsappCallActions from "../../hooks/calls/useWhatsappCallActions";

function formatElapsedDuration(totalSeconds) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(safeSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * ActiveCallCard component.
 *
 * Displays active call information and allows muting the microphone.
 */
const ActiveCallCard = memo(({ callInfoData, onClose }) => {
  const { t } = useTranslation('Fonema');
  const { provider } = useWhatsappCallsRuntime();
  const callUiEvents = useCallUiEvents();
  const voiceIsMuted = useSelector((state) => (
    provider === 'voice' ? state.voiceReducer.isMuted : false
  ));

  // UI Layer
  const { buttonsDisabled, disableButtons } = useButtonsDisabled();

  // Local state for mute
  const [isMuted, setIsMuted] = useState(false);

  // Actions Layer
  const { endCall, toggleMic } = useWhatsappCallActions({ callInfoData, disableButtons });

  const callInfoDataRef = useRef(callInfoData);
  callInfoDataRef.current = callInfoData;

  const clearCallReferences = useCallback(() => {
    if (callUiEvents?.clearCallReferences) {
      callUiEvents.clearCallReferences();
      return;
    }
    window.clearFonemaCallReferences?.();
  }, [callUiEvents]);

  // If the agent reloads or closes the tab, hang up via SDK to not leave the customer online.
  useEffect(() => {
    const handlePageHide = (event) => {
      if (event.persisted) return;
      const callId = callInfoDataRef.current?.callId;
      if (!callId || !window.FonemaWhatsappAudio?.endCall) return;
      Promise.resolve(window.FonemaWhatsappAudio.endCall(callId)).catch(() => {});
      clearCallReferences();
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [clearCallReferences]);

  // UI Layer: Duration of call reusable and automatic unmount when it ends
  const { callDuration } = useFonemaCallDuration();
  const [voiceDuration, setVoiceDuration] = useState('00:00:00');
  const voiceStartTimeRef = useRef(Date.now());

  useEffect(() => {
    if (provider !== 'voice') return undefined;

    voiceStartTimeRef.current = Date.now();
    const updateVoiceDuration = () => {
      const elapsedSeconds = Math.floor((Date.now() - voiceStartTimeRef.current) / 1000);
      setVoiceDuration(formatElapsedDuration(elapsedSeconds));
    };

    updateVoiceDuration();
    const intervalId = setInterval(updateVoiceDuration, 1000);
    return () => clearInterval(intervalId);
  }, [provider, callInfoData?.callId]);

  const activeDuration = provider === 'voice' ? voiceDuration : callDuration;

  useEffect(() => {
    if (provider !== 'voice') return undefined;
    setIsMuted(voiceIsMuted);
    return undefined;
  }, [provider, voiceIsMuted]);

  // Handler to toggle mute
  const handleToggleMic = useCallback(() => {
    const muted = toggleMic();
    setIsMuted(muted);
  }, [toggleMic]);

  const handleEndCall = useCallback(async () => {
    try {
      await endCall();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      clearCallReferences();
      onClose();
    }
  }, [endCall, onClose, clearCallReferences]);

  return (
    <div
      className="tw-wrapper"
      style={{
        left: 64,
        bottom: 8,
        position: "fixed",
        outline: 'none',
        maxWidth: 320,
        minWidth: 220,
        margin: 0,
        zIndex: 9999
      }}
      tabIndex={-1}
      aria-label={t('active_call')}
    >
      <div className="tw-bg-blue-950 tw-text-white tw-select-none tw-p-4 tw-rounded-xl tw-border-transparent">
        <div className="tw-flex tw-flex-row tw-items-center tw-text-blue-100">
          <div className="tw-bg-white tw-rounded-full tw-p-1 tw-flex tw-items-center tw-justify-center tw-border tw-border-blue-100" style={{ width: 32, height: 32 }}>
            <IconMC name="phone-call" size="base" className="tw-text-[#50B77D]" />
          </div>
          <div className="tw-ml-2">
            <div className="tw-text-sm tw-font-medium">{t('active_call')}</div>
            <div className="tw-text-default tw-font-normal tw-text-blue-100">{activeDuration}</div>
          </div>
        </div>
        <CallCustomerData customer={callInfoData?.customer} phone={callInfoData?.callerNumber} />
        <div className="tw-flex tw-gap-1 tw-mt-2">
          <ButtonComponent
            icon={isMuted ? "mic-off-outline" : "mic-outline"}
            iconClassName="!tw-size-[16px] !tw-text-white"
            variant="SECONDARY"
            disabled={buttonsDisabled}
            className="!tw-bg-blue-800 hover:!tw-bg-blue-900"
            onClick={handleToggleMic}
          />
          <ButtonComponent
            text={t('end_call')}
            className="tw-w-full"
            icon="phone-call-end"
            iconPosition="left"
            iconClassName="!tw-size-[16px]"
            variant="DANGER"
            onClick={handleEndCall}
            disabled={buttonsDisabled}
          />
        </div>
      </div>
    </div>
  );
});

export default ActiveCallCard;
